import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useStoreState } from 'easy-peasy';
import * as bannersApi from '~/fetch/banners';
import { IBanner } from '~/utils/bannerUtils';
export type { IBanner } from '~/utils/bannerUtils';
import { queryKeys } from './queryKeys';
import { toast } from 'react-toastify';

async function parseResponse<T>(
  response: Response,
  extract: (data: any) => T
): Promise<T> {
  if (!response.ok) {
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      // ignore parse errors
    }
    const err: any = new Error(`Request failed: ${response.status}`);
    err.status = response.status;
    err.body = body;
    throw err;
  }
  const data = await response.json();
  return extract(data);
}

export const useBanners = (solutionId?: string) => {
  const solution = useStoreState((state: any) => state.auth.solution);
  const effectiveId = solutionId ?? solution?.id;

  return useQuery({
    queryKey: queryKeys.banners.list(effectiveId),
    queryFn: async () => {
      const response = await bannersApi.list({ solutionId: effectiveId });
      return parseResponse<IBanner[]>(response as any, (d) => d.banners ?? []);
    },
    enabled: !!effectiveId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useBannerSchema = () =>
  useQuery({
    queryKey: queryKeys.banners.schema(),
    queryFn: async () => {
      const response = await bannersApi.schema();
      return parseResponse(response as any, (d) => d.fields ?? null);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useBannerEditSchema = (bannerId: string) =>
  useQuery({
    queryKey: queryKeys.banners.editSchema(bannerId),
    queryFn: async () => {
      const response = await bannersApi.editSchema({ id: bannerId });
      return parseResponse(response as any, (d) => d.fields ?? null);
    },
    enabled: !!bannerId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useCreateBanner = () => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();

  return useMutation({
    mutationFn: async (data: Omit<IBanner, 'id'>) => {
      const response = await bannersApi.add(data);
      return parseResponse<IBanner>(response as any, (d) => d.banner);
    },
    onSuccess: () => {
      toast.success(
        formatMessage({
          id: 'banners-created-toast',
          defaultMessage: 'Banner created',
        })
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.banners.all });
    },
    onError: () => {
      toast.error(
        formatMessage({
          id: 'banners-create-error-toast',
          defaultMessage: 'Error creating new Banner',
        })
      );
    },
  });
};

export const useUpdateBanner = () => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();

  return useMutation({
    mutationFn: async (data: IBanner) => {
      const response = await bannersApi.update(data);
      return parseResponse<IBanner>(response as any, (d) => d.banner);
    },
    onSuccess: () => {
      toast.success(
        formatMessage({
          id: 'banners-updated-toast',
          defaultMessage: 'Banner updated',
        })
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.banners.all });
    },
    onError: () => {
      toast.error(
        formatMessage({
          id: 'banners-update-error-toast',
          defaultMessage: 'Error updating Banner',
        })
      );
    },
  });
};

export const useDeleteBanner = () => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();

  return useMutation({
    mutationFn: async (data: Pick<IBanner, 'id'>) => {
      const response = await bannersApi.remove(data);
      if (!(response as any).ok) throw new Error('Delete failed');
    },
    onSuccess: () => {
      toast.success(
        formatMessage({
          id: 'banners-deleted-toast',
          defaultMessage: 'Banner deleted',
        })
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.banners.all });
    },
    onError: () => {
      toast.error(
        formatMessage({
          id: 'banners-delete-error-toast',
          defaultMessage: 'Error deleting Banner',
        })
      );
    },
  });
};

export const useToggleBannerPublished = () => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();

  return useMutation({
    mutationFn: async (data: Pick<IBanner, 'id' | 'ispublished'>) => {
      const response = await bannersApi.updateIsPublished(data);
      return parseResponse<IBanner>(response as any, (d) => d.banner);
    },
    onSuccess: (banner) => {
      toast.success(
        banner.ispublished
          ? formatMessage({
              id: 'banners-published-toast',
              defaultMessage: 'Banner published',
            })
          : formatMessage({
              id: 'banners-unpublished-toast',
              defaultMessage: 'Banner un-published',
            })
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.banners.all });
    },
    onError: () => {
      toast.error(
        formatMessage({
          id: 'banners-publish-error-toast',
          defaultMessage: 'Error publishing Banner',
        })
      );
    },
  });
};

export const useDisplayBanners = () => {
  const solutionId = useStoreState((state: any) => state.auth.solution?.id);

  return useQuery({
    queryKey: queryKeys.banners.display(),
    queryFn: async () => {
      // No `solution` param: cream reads it as the management listing (managers
      // only, drafts included, platform-wide excluded). Bare /banners returns
      // the live banners for all the user's solutions plus platform-wide ones,
      // as one page of cream's default 10, earliest start first.
      const response = await bannersApi.list();
      return parseResponse<IBanner[]>(response as any, (d) => d.banners ?? []);
    },
    enabled: !!solutionId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: false,
  });
};
